<script setup>
import { defineProps } from 'vue';
import { handleMarketplaceUrl } from '@/services/api';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  badge: {
    type: String,
    default: ''
  },
  content: {
    type: String,
    default: ''
  },
  footerText: {
    type: String,
    default: ''
  },
  imageSrc: {
    type: String,
    default: ''
  },
  imageAlt: {
    type: String,
    default: ''
  },
  actionText: {
    type: String,
    default: 'Action'
  },
  reverse: {
    type: Boolean,
    default: false
  },
  redirectTo: {
    type: String,
    default: '/explore'
  }
});

const handleRedirect = () => {
  const targetPath = props.redirectTo || '/explore';

  if (targetPath.startsWith('http://') || targetPath.startsWith('https://')) {
    window.open(targetPath, '_blank');
    return;
  }

  const normalizedPath = targetPath.startsWith('/') ? targetPath : `/${targetPath}`;
  window.open(handleMarketplaceUrl() + normalizedPath, '_blank');
};




</script>
<template>
  <div class="custom-card rounded-xl">
    <!-- LEFT SECTION -->
    <div 
      class="left-section flex flex-col gap-6"
      :class="{ 'order-2': reverse }"
    >
      <div class="header">
        <h3 class="title">{{ title }}</h3>
      </div>
      <div 
        class="image-border-container flex overflow-hidden relative"
        :style="{ 
          background: 'linear-gradient(to right, #434343, #999999)',
          padding: '4px',
          boxShadow: '-10px 10px 20.8px -1px rgba(255, 255, 255, 0.25)'
        }"
      >
        <img 
          v-if="imageSrc" 
          :src="imageSrc" 
          :alt="imageAlt || ''"
          class="square-image"
          style="overflow: hidden;"
          loading="lazy"
        />
        <slot name="image">
          <div v-if="!imageSrc" class="square-image placeholder" style="overflow: hidden;">
            <span>Placeholder</span>
          </div>
        </slot>
      </div>
   
    </div>
    
    <!-- RIGHT SECTION -->
    <div 
      class="right-section"
      :class="{ 'order-1': reverse }"
    >
       
      
      <div class="content">
        <slot name="content">
          {{ content }}
        </slot>
      </div>
      
        <div class="button-section">
        <button class="button" @click="handleRedirect">
          {{ actionText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
  gap: 3rem;
  position: relative;

  border: 1px solid #0F0F0F;
  
  padding: 2rem;
  overflow: hidden;
  min-height: 0;
}

.custom-card > * {
  position: relative;
  z-index: 1;
}



.left-section.order-2 {
  order: 2;
}

.right-section.order-1 {
  order: 1;
}

.left-section {
  min-width: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1.2;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  background: linear-gradient(to bottom right, white, rgba(255, 255, 255, 0.493));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
}

.content {
  
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: justify;
  text-align-last: left;
  text-justify: inter-word;
  max-width: 100%;
  width: 100%;
}

.footer {
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
}

.button-section {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button {
  padding: 0.75rem 1.5rem;
  border-radius: 1rem;
  font-size: 1rem;
  font-weight: 600;
  background: #d32f00;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  border: none;
  outline: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.button:hover {
  background: #d32f00;
  color: #ffffff;
  filter: brightness(1.15);
}

.button:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

.right-section {
  margin-top: 0;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6rem;
  align-items: center;
  width: 100%;
}

.image-border-container {
  box-shadow: 
    0 0 10px rgba(255, 255, 255, 0.3),
    0 0 20px rgba(255, 255, 255, 0.2),
    0 0 30px rgba(255, 255, 255, 0.1);
  border-radius: 2.75rem;
}

@keyframes borderGlow {
  0%, 100% {
    box-shadow: 
      0 0 10px rgba(255, 255, 255, 0.3),
      0 0 20px rgba(255, 255, 255, 0.2),
      0 0 30px rgba(255, 255, 255, 0.1);
  }
  50% {
    box-shadow: 
      0 0 15px rgba(255, 255, 255, 0.4),
      0 0 25px rgba(255, 255, 255, 0.3),
      0 0 35px rgba(255, 255, 255, 0.2);
  }
}

.square-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  border: none;
  object-fit: contain;
  display: block;
  border-radius: 2.5rem;
}

.square-image.placeholder {
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  border: none;
  min-height: 200px;
  width: 100%;
  border-radius: 2.5rem;
}

@media (max-width: 1024px) {
  .custom-card {
    gap: 1.25rem;
    padding: 1.25rem;
  }
  
  .right-section {
    gap: 1.75rem;
  }
  
  .square-image {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .custom-card {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
  }
  
  .left-section,
  .left-section.order-2 {
    order: 1 !important;
  }
  
  .right-section,
  .right-section.order-1 {
    order: 2 !important;
  }
  
  .left-section {
    gap: 1.25rem;
  }
  
  .right-section {
    margin-top: 0;
    gap: 1.5rem;
    width: 100%;
  }
  
  .title {
    font-size: 1.125rem;
  }
  
  .content {
    font-size: 0.95rem;
  }
  
  .button {
    padding: 0.625rem 1.25rem;
    font-size: 0.95rem;
  }
}

@media (max-width: 640px) {
  .custom-card {
    gap: 1.25rem;
    padding: 1rem;
    border-radius: 0.75rem;
    display: flex;
    flex-direction: column;
  }
  
  .left-section,
  .left-section.order-2 {
    order: 1 !important;
  }
  
  .right-section,
  .right-section.order-1 {
    order: 2 !important;
  }
  
  .left-section {
    gap: 1rem;
  }
  
  .right-section {
    gap: 1.25rem;
  }
  
  .title {
    font-size: 1rem;
    letter-spacing: 0.05em;
  }
  
  .content {
    font-size: 0.9rem;
    text-align: center;
  }
  
  .button {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    border-radius: 0.75rem;
  }
  
  .square-image {
    max-width: 100%;
  }
  
  .square-image.placeholder {
    min-height: 180px;
  }
}

@media (max-width: 480px) {
  .custom-card {
    gap: 1rem;
    padding: 0.875rem;
    display: flex;
    flex-direction: column;
  }
  
  .left-section,
  .left-section.order-2 {
    order: 1 !important;
  }
  
  .right-section,
  .right-section.order-1 {
    order: 2 !important;
  }
  
  .left-section {
    gap: 0.875rem;
  }
  
  .right-section {
    gap: 1rem;
  }
  
  .title {
    font-size: 0.9rem;
    letter-spacing: 0.03em;
  }
  
  .content {
    font-size: 0.85rem;
  }
  
  .button {
    padding: 0.5rem 0.875rem;
    font-size: 0.85rem;
  }
  
  .square-image.placeholder {
    min-height: 150px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .button {
    transition: none;
  }
  
  .image-border-container {
    animation: none;
  }
}

@media (max-width: 1024px) and (orientation: landscape) {
  .custom-card {
    grid-template-columns: 1fr 1fr;
  }
  
  .right-section {
    gap: 1.5rem;
  }
}
</style>
